import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, ClickAwayListener, Popper } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import dayjs from 'dayjs';
import 'dayjs/locale/en';

import './CustomStyledDatePicker.scss';

import { CHOOSE_DATE } from '../../Constants/strings';
import CalendarIcon from '../../Assets/images/svg/dar_calendar_blank_gray.svg';

const DEFAULT_FORMAT = 'DD/MM/YYYY';

const CustomStyledDatePicker = ({
  value,
  onChange,
  label,
  placeholder,
  minDate,
  maxDate,
  isDisabled = false,
  error = false,
  helperText,
  displayFormat = DEFAULT_FORMAT,
  calendarSx = {},
  popperPlacement = 'bottom-start',
  disablePopperFlip = false,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  /* -------------------------------------------------------------------------- */
  /*                             Calendar Styles                               */
  /* -------------------------------------------------------------------------- */

  const calendarStyles = {
    bgcolor: '#fff',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '320px',
    minWidth: '260px',
    fontFamily: 'Mukta, sans-serif',
    overflow: 'hidden',
    boxShadow:
      '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',

    '& .MuiPickersLayout-root': {
      maxHeight: '350px',
    },

    '& .MuiPickersLayout-contentWrapper': {
      overflowY: 'auto',
      maxHeight: '320px',
    },

    '& .MuiYearCalendar-root': {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '8px',
      padding: '16px',
    },

    '& .MuiPickersYear-yearButton': {
      fontFamily: 'Mukta, sans-serif',
      fontSize: '14px',
      height: '36px',
      width: '100%',
    },

    '& .MuiTypography-root': {
      fontFamily: 'Mukta, sans-serif',
    },

    '& .MuiPickersCalendarHeader-label': {
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Mukta, sans-serif',
    },

    '& .MuiPickersDay-root': {
      fontSize: '12px',
      fontFamily: 'Mukta, sans-serif',
    },

    '& .MuiDayCalendar-weekDayLabel': {
      fontSize: '12px',
      fontWeight: 600,
      fontFamily: 'Mukta, sans-serif',
    },

    '& .MuiPickersArrowSwitcher-button': {
      fontFamily: 'Mukta, sans-serif',
    },
  };

  const mergedCalendarStyles = {
    ...calendarStyles,
    ...calendarSx,
    '& .MuiPickersCalendarHeader-label': {
      ...calendarStyles['& .MuiPickersCalendarHeader-label'],
      ...(calendarSx['& .MuiPickersCalendarHeader-label'] || {}),
    },
  };

  /* -------------------------------------------------------------------------- */
  /*                               Derived Value                               */
  /* -------------------------------------------------------------------------- */

  const displayValue = value
    ? dayjs(value).format(displayFormat)
    : placeholder || CHOOSE_DATE;

  /* -------------------------------------------------------------------------- */
  /*                                Handlers                                    */
  /* -------------------------------------------------------------------------- */

  const handleOpen = () => {
    if (!isDisabled) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (date) => {
    onChange(date);
    handleClose();
  };

  /* -------------------------------------------------------------------------- */
  /*                                  Render                                    */
  /* -------------------------------------------------------------------------- */

  const buttonClassName = `
    custom-styled-date-field
    ${isDisabled ? 'is-disabled' : ''}
    ${error ? 'is-error' : ''}
  `.trim();

  return (
    <div className="custom-styled-date-container">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <button
          ref={anchorRef}
          type="button"
          className={buttonClassName}
          onClick={handleOpen}
          aria-label={label}
          disabled={isDisabled}
        >
          {label && (
            <span className="custom-styled-date-prefix">
              {label}
            </span>
          )}

          <span className="custom-styled-date-value">
            {displayValue}
          </span>

          <span
            className="custom-styled-date-icon-wrapper"
            aria-hidden="true"
          >
            <img
              src={CalendarIcon}
              alt=""
              className="custom-styled-date-icon"
            />
          </span>
        </button>

        {helperText && (
          <div
            className={`custom-styled-date-helper ${
              error ? 'is-error' : ''
            }`}
          >
            {helperText}
          </div>
        )}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement={popperPlacement}
          modifiers={[
            {
              name: 'flip',
              enabled: !disablePopperFlip,
            },
          ]}
          className="custom-styled-date-popper"
          style={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown" touchEvent="onTouchStart">
            <Box sx={{ padding: 1, maxWidth: '100vw', overflowX: 'auto' }}>
              <DateCalendar
                value={value}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                dayOfWeekFormatter={(day) =>
                  day.format('ddd')
                }
                sx={mergedCalendarStyles}
              />
            </Box>
          </ClickAwayListener>
        </Popper>
      </LocalizationProvider>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                PropTypes                                   */
/* -------------------------------------------------------------------------- */

CustomStyledDatePicker.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  isDisabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  displayFormat: PropTypes.string,
  calendarSx: PropTypes.object,
  popperPlacement: PropTypes.string,
  disablePopperFlip: PropTypes.bool,
};

export default CustomStyledDatePicker;
